import { useEffect, useState, useCallback } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { OnboardingModal } from "@/components/onboarding/OnboardingModal";
import { ws } from "@/lib/ws";
import type { OrgMeta, UserMeta } from "@shared/protocol";

export interface Session {
  user: UserMeta;
  org: OrgMeta;
  orgs: OrgMeta[];
  /** Platform-wide feature flags (see shared/protocol.ts's FEATURE_FLAG_DEFS) — a
   * plain key -> enabled map, cheap enough to hand to every connected client. */
  flags: Record<string, boolean>;
  switchOrg: (orgId: string) => void;
}

export function AppShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState<Omit<Session, "switchOrg"> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const switchOrg = useCallback((orgId: string) => {
    ws.send({ type: "org.select", org_id: orgId });
  }, []);

  useEffect(() => {
    let cancelled = false;
    const offAuth = ws.on("auth.ok", (msg) => {
      if (cancelled) return;
      setSession({
        user: msg.user,
        org: msg.org,
        orgs: msg.orgs,
        flags: msg.flags,
      });
      // A user who has never finished (or skipped) getting-started lands on
      // /keys instead of the dashboard — the API keys page is where the
      // onboarding modal below actually lives/completes. Only redirect off
      // the default landing routes so a direct deep link elsewhere isn't
      // hijacked.
      if (!msg.user.onboarding_completed) {
        setShowOnboarding(true);
        if (location.pathname === "/dashboard" || location.pathname === "/") {
          navigate("/keys", { replace: true });
        }
      }
    });
    const offSwitch = ws.on("org.switched", (msg) => {
      if (cancelled) return;
      setSession((s) => (s ? { ...s, org: msg.org } : s));
    });
    ws.connect().catch((err) => {
      setError(err instanceof Error ? err.message : "connection failed");
      navigate("/login");
    });
    return () => {
      cancelled = true;
      offAuth();
      offSwitch();
    };
  }, [navigate]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8 text-sm text-muted-foreground">
        {error}
      </div>
    );
  }
  if (!session) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8 text-sm text-muted-foreground">
        Connecting…
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <ImpersonationBanner />
      <div className="flex flex-1 min-h-0">
        <Sidebar
          org={session.org}
          orgs={session.orgs}
          flags={session.flags}
          switchOrg={switchOrg}
        />
        <div className="flex-1 flex flex-col">
          <TopBar user={session.user} />
          <main className="flex-1 p-6 bg-muted/20">
            <SessionContext.Provider value={{ ...session, switchOrg }}>
              <Outlet />
            </SessionContext.Provider>
          </main>
        </div>
      </div>
      <OnboardingModal
        open={showOnboarding}
        onDone={() => {
          setShowOnboarding(false);
          setSession((s) =>
            s ? { ...s, user: { ...s.user, onboarding_completed: true } } : s,
          );
        }}
      />
    </div>
  );
}

import { createContext, useContext } from "react";
const SessionContext = createContext<Session | null>(null);
export function useSession(): Session {
  const s = useContext(SessionContext);
  if (!s) throw new Error("useSession used outside AppShell");
  return s;
}
